import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import DOMPurify from 'dompurify'

interface CompanyInvitationProps {
  invitationToken: string
  removeInvitationToken: () => void
}

const CompanyInvitation: React.VFC<CompanyInvitationProps> = ({ invitationToken, removeInvitationToken }) => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [companyName, setCompanyName] = useState('')

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const response: { companyName: string } = await authFetch({
          path: `users/companies/invites/${invitationToken}`,
          method: 'GET',
        })
        setCompanyName(response?.companyName)
      } catch (err) {
        enqueueErrorStack(t('user.company.invitation.token.error.companyName'))
      }
    }

    fetchData()
  }, []) // eslint-disable-line

  const onAcceptInvite = (): void => {
    setIsLoading(true)
    authFetch({
      path: `users/companies/invites/${invitationToken}/accept`,
      method: 'POST',
    })
      .then(() => {
        enqueueSuccessStack(t('user.company.invitation.accept.success'))
        removeInvitationToken()
        navigate('/user')
        dispatch(refreshApiView())
      })
      .catch((e: AuthFetchError) => {
        switch (e.response?.data.errno) {
          case FetchErrorCodes.IncorrectUser: {
            enqueueErrorStack(t('user.company.invitation.accept.error.userMismatch'))
            break
          }
          case FetchErrorCodes.OnlyUserCanAccept: {
            enqueueErrorStack(t('user.company.invitation.accept.error.user-level'))
            break
          }
          case FetchErrorCodes.UserAlreadyInCompany: {
            enqueueErrorStack(t('user.company.invitation.accept.error.user-already-in-company'))
            break
          }
          default: {
            enqueueErrorStack(t('user.company.invitation.accept.error.general', { error: e.response?.data.error }))
            break
          }
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <Dialog
      title={t('user.company.invitation.accept.title')}
      onClose={() => {
        navigate('/')
      }}
      actions={{
        primary: {
          name: t('user.company.invitation.accept.buttonText'),
          onClick: onAcceptInvite,
          isLoading,
        },
        secondary: [
          {
            name: t('user.company.invitation.cancel.buttonText'),
            onClick: () => {
              removeInvitationToken()
              navigate('/')
            },
          },
        ],
      }}
    >
      <Typography dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('user.company.invitation.accept.text', { companyName })) }} />
    </Dialog>
  )
}

export default CompanyInvitation
